import {
  Heading,
  Tabs,
  TabsList,
  TabsTrigger,
  TabsContent,
  Text,
  Picture,
  Link,
  Button,
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  LoadingDots,
} from '../../components/index.js'
import { DownArrow } from '../HeaderNavigation/icons/index.js'
import { useMemo, useRef, useState } from 'react'
import { cn, atobID, highlightSearchWord } from '../../helpers/utils.js'
import { useAiuiContext } from '../AiuiProvider/index.js'
import { formatVariantPrice } from '../ShelfDisplay/shelfDisplay.js'
import type {
  SearchPageProps,
  SortKey,
  SearchProductItemProps,
  SearchBlogItemProps,
  SearchPageItemProps,
  SearchRecommendProductsProps,
} from './types'
import { SearchPageTabType } from './types.js'
import { getLocalizedPath } from '../../helpers/utils.js'
import { replaceTemplate } from '../Listing/utils/textFormat.js'

/**
 * 搜索商品
 */
const SearchProductItem = ({
  product,
  onSecondaryButton,
  onPrimaryButton,
  searchValue,
  variantData,
  shopNowLoadingProductId,
}: SearchProductItemProps) => {
  const variant = useMemo(() => variantData || product?.variants?.[0], [product, variantData])
  const { locale = 'us', copyWriting } = useAiuiContext()

  // 判断当前产品是否在加载中
  const isCurrentProductLoading = shopNowLoadingProductId === product?.id

  const listingLink = useMemo(() => {
    return getLocalizedPath(
      `/products/${product?.handle}?variant=${atobID(variant?.id as string)}?ref=search_result_${searchValue?.toLowerCase()}`,
      locale
    )
  }, [product?.handle, variant?.id, searchValue, locale])

  const coupon = variant?.coupons?.[0]

  const { price, basePrice } = formatVariantPrice({
    locale: locale,
    amount: coupon ? coupon.variant_price4wscode : (variant?.price?.amount ?? variant?.price),
    baseAmount: coupon ? (variant?.price?.amount ?? variant?.price) : 0,
    currencyCode: product?.price?.currencyCode || 'USD',
  })

  const tags = useMemo(() => {
    return product?.tags
      ?.filter?.((item: any) => item?.startsWith?.('CLtag'))
      ?.map?.((item: any) => item?.replace?.('CLtag:', ''))
      ?.slice?.(0, 2)
  }, [product?.tags])

  return (
    <div className="rounded-image-text ipc_search_product_item lg-desktop:h-[480px] desktop:h-[384px] laptop:h-[360px] relative h-auto overflow-hidden">
      {product?.type === 'advertising' && product?.advertisingLink ? (
        <Link href={getLocalizedPath(product?.advertisingLink, locale)} className="h-full">
          <Picture
            source={`${product?.advertisingBgImg}`}
            className="size-full"
            imgClassName="size-full object-cover"
          />
          <div className="desktop:p-6 absolute inset-0 z-[2] p-4 [&_img]:hover:scale-110 [&_img]:hover:transition-all [&_img]:hover:duration-500">
            <Text
              html={product?.advertisingTitle}
              className={cn('desktop:text-base text-sm font-bold leading-[1.2]', {
                'text-white': product?.advertisingTheme === 'light',
                'text-info-primary': product?.advertisingTheme === 'dark',
              })}
            />
            {product?.advertisingSubtitle && (
              <Heading
                size={3}
                html={product?.advertisingSubtitle}
                className={cn('laptop:line-clamp-3 mt-2 line-clamp-4 font-bold', {
                  'text-white': product?.advertisingTheme === 'light',
                  'text-info-primary': product?.advertisingTheme === 'dark',
                })}
              />
            )}
          </div>
        </Link>
      ) : (
        <a
          href={listingLink}
          className="tablet:hover:bg-info-white desktop:p-6 desktop:pt-4 tablet:p-4 flex h-full flex-col bg-[#EAEAEC] px-2 py-4 no-underline duration-300"
        >
          <div className="ipc_search_product_item_image">
            <Picture
              className="lg-desktop:size-[196px] mx-auto size-[138px]"
              source={`${variant?.image?.url || product?.images?.[0]?.url}}`}
              alt={product?.title}
              imgClassName="object-cover size-full"
            />
          </div>
          <div className="ipc_search_product_item_content desktop:mt-2 lg-desktop:mt-3 desktop:gap-6 mt-1 flex flex-1 flex-col justify-between gap-4">
            <div className="flex-1">
              <div className="lg-desktop:h-[28px] mb-2 flex h-[24px] gap-2">
                {tags?.map((tag: string, index: number) => (
                  <Text
                    key={index}
                    as="p"
                    html={tag}
                    className="text-brand-0 lg-desktop:px-2 lg-desktop:!leading-[28px] h-full rounded-full border-[1.6px] border-[#00BEFA] px-[6px] text-sm font-bold !leading-[22px]"
                  />
                ))}
              </div>
              <Text
                className="lg-desktop:text-2xl tablet:line-clamp-2 line-clamp-3 text-xl font-bold !leading-[1.2]"
                weight="bold"
                html={highlightSearchWord(product?.title || '', searchValue || '')}
              />
              <Text
                as="p"
                className="lg-desktop:text-[18px] tablet:line-clamp-1 mt-1 line-clamp-2 text-sm font-bold"
                html={highlightSearchWord(product?.description, searchValue || '')}
              />
            </div>
            <div>
              <div className="mb-2 flex items-center">
                {!variant?.availableForSale ? (
                  <div className="text-info-primary tablet:text-xl lg-desktop:text-2xl text-[18px] font-bold">
                    {copyWriting?.soldOutText || 'Sold Out'}
                  </div>
                ) : (
                  <>
                    <div className="final-price text-info-primary tablet:text-xl lg-desktop:text-2xl text-[18px] font-bold">
                      {variant?.availableForSale ? price || '' : ''}
                    </div>
                    <div className="origin-price tablet:text-xl lg-desktop:text-2xl text-info-secondary ml-1 text-[18px] font-bold line-through">
                      {variant?.availableForSale ? basePrice || '' : ''}
                    </div>
                  </>
                )}
              </div>
              <div
                className={cn(
                  'ipc_search_product_item_button_group',
                  'lg-desktop:gap-3 laptop:flex-row flex flex-col items-center gap-2'
                )}
              >
                {copyWriting?.learnMoreText && (
                  <Button
                    variant="secondary"
                    className="laptop:w-auto w-full"
                    size="lg"
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      onSecondaryButton?.()
                    }}
                  >
                    {copyWriting?.learnMoreText}
                  </Button>
                )}
                {copyWriting?.shopNowText && (
                  <Button
                    variant="primary"
                    className="laptop:w-auto w-full"
                    size="lg"
                    disabled={!variant?.availableForSale}
                    loading={isCurrentProductLoading}
                    onClick={e => {
                      e.preventDefault()
                      e.stopPropagation()
                      onPrimaryButton?.()
                    }}
                  >
                    {copyWriting?.shopNowText}
                  </Button>
                )}
              </div>
            </div>
          </div>
        </a>
      )}
    </div>
  )
}

/**
 * 搜索文章
 */
const SearchBlogItem = ({ blog, searchValue }: SearchBlogItemProps) => {
  const { copyWriting, locale = 'us' } = useAiuiContext()

  const articleLink = useMemo(
    () => getLocalizedPath(`/blogs/${blog?.blog?.handle}/${blog?.handle.replace('storefront-', '')}`, locale),
    [blog, locale]
  )

  return (
    <div className="ipc_search_blog_item border-b border-[#E4E5E6] py-6">
      <div className="ipc_search_blog_item_content laptop:flex-row-reverse laptop:gap-8 desktop:gap-16 flex flex-col gap-4">
        <div className="rounded-image-text laptop:flex-[440] desktop:flex-[540] desktop:h-[280px] h-[240px] overflow-hidden">
          <Picture
            source={blog?.image?.url}
            className="size-full"
            imgClassName="size-full object-cover"
            alt={blog?.title}
          />
        </div>
        <div className="laptop:flex-[424] desktop:flex-[704] lg-desktop:flex-[1056] flex flex-col justify-center">
          <Heading size={3} html={highlightSearchWord(blog?.title, searchValue)} />
          <Text
            html={highlightSearchWord(blog?.content, searchValue)}
            className="lg-desktop:text-[18px] desktop:text-[16px] mt-1 line-clamp-2 text-sm font-bold leading-[1.4]"
          />
          <div className="desktop:mt-6 mt-4">
            <Button as="a" href={articleLink} variant="secondary" size="lg">
              {copyWriting?.learnMoreText || 'Learn More'}
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * 搜索页面
 */
const SearchPageItem = ({ searchValue, page }: SearchPageItemProps) => {
  const { copyWriting, locale = 'us' } = useAiuiContext()
  const pageLink = useMemo(() => getLocalizedPath(`/${page?.handle}`, locale), [page, locale])
  return (
    <div className="ipc_search_page_items border-b border-[#E4E5E6] py-6">
      <Heading size={3} html={highlightSearchWord(page?.name, searchValue)} />
      <Text
        as="p"
        html={highlightSearchWord(page?.bodySummary || '', searchValue)}
        className="lg-desktop:text-[18px] desktop:text-[16px] mt-1 text-sm font-bold leading-[1.4]"
      />
      <Button as="a" href={pageLink} variant="secondary" size="lg" className="desktop:mt-6 mt-4">
        {copyWriting?.learnMoreText || 'Learn More'}
      </Button>
    </div>
  )
}

/**
 * 搜索结果为空
 */
const SearchPageNoResult = ({ url, label }: { url: string; label: string }) => {
  return (
    <div className="flex flex-col items-center justify-center gap-4">
      <Picture source={url} className="h-[180px] w-[240px]" imgClassName="object-cover" />
      <Text html={label} className="desktop:text-[18px] text-sm font-bold leading-[1.4]" />
    </div>
  )
}

/**
 * 搜索推荐商品
 */
const SearchRecommendProducts = ({
  products,
  title,
  buildProps,
  onSecondaryButton,
  onPrimaryButton,
}: SearchRecommendProductsProps) => {
  const { products: buildProducts } = buildProps || {}
  console.log(buildProducts, 'buildProducts')
  return (
    <div className="ipc_search_recommend_product tablet:pt-[80px] laptop:pt-[96px] desktop:pt-[112px] lg-desktop:pt-[128px] pt-[64px]">
      <Heading size={4} weight="bold" html={title} />
      <div className=" tablet:grid-cols-3 desktop:grid-cols-4 desktop:gap-4 mt-6 grid grid-cols-2 gap-x-3 gap-y-4">
        {products?.map((product: any, index: number) => {
          const buildProduct = buildProducts?.find((buildProduct: any) => buildProduct.handle === product.handle)
          if (!buildProduct) return null
          const variant = buildProduct?.variants?.find((variant: any) => variant.sku === product.sku)
          return (
            <SearchProductItem
              product={buildProduct}
              variantData={variant}
              key={product.sku + index}
              onSecondaryButton={() => onSecondaryButton?.()}
              onPrimaryButton={() => onPrimaryButton?.()}
            />
          )
        })}
      </div>
    </div>
  )
}

/**
 * 搜索页面
 */
const SearchPage = ({
  searchResult,
  searchValue,
  data,
  onSecondaryButton,
  onPrimaryButton,
  // loadMore,
  onChangeSort,
  loading,
  shopNowLoadingProductId,
  searchResultCount,
  onChangeTab,
  buildProps,
  className,
  ...rest
}: SearchPageProps) => {
  const [currentTab, setCurrentTab] = useState<any>(data?.tabs?.[0])
  const isInsertAdvertising = useRef(false)
  const [isOpenSort, setIsOpenSort] = useState(false)
  const [currentSortKey, setCurrentSortKey] = useState<any>({
    [SearchPageTabType.PRODUCTS]: data?.tabs?.find((tab: any) => tab.tabType === SearchPageTabType.PRODUCTS)
      ?.sortKeys?.[0],
    [SearchPageTabType.BLOGS]: data?.tabs?.find((tab: any) => tab.tabType === SearchPageTabType.BLOGS)?.sortKeys?.[0],
    [SearchPageTabType.PAGES]: data?.tabs?.find((tab: any) => tab.tabType === SearchPageTabType.PAGES)?.sortKeys?.[0],
  })

  const searchProductsResult = useMemo(() => {
    return searchResult?.products || []
  }, [searchResult])

  const searchBlogsResult = useMemo(() => {
    return searchResult?.blogs || []
  }, [searchResult])

  const searchPagesResult = useMemo(() => {
    return searchResult?.pages || []
  }, [searchResult])

  const totalCount = useMemo(() => {
    return Object.values(searchResultCount || {}).reduce((acc, curr) => acc + curr, 0)
  }, [searchResultCount])

  const searchProductsResultWithAdvertising = useMemo(() => {
    const advertisingData = {
      advertisingBgImg: `${currentTab?.advertisingBgImg?.url}, ${currentTab?.advertisingMobBgImg?.url} 768`,
      advertisingTitle: currentTab?.advertisingTitle,
      advertisingSubtitle: currentTab?.advertisingSubtitle,
      advertisingLink: currentTab?.advertisingLink,
      advertisingTheme: currentTab?.advertisingTheme,
      type: 'advertising',
    }
    if (!isInsertAdvertising.current && !!searchProductsResult?.length) {
      if (searchProductsResult?.length >= 8) {
        searchProductsResult?.splice(7, 0, advertisingData)
        isInsertAdvertising.current = true
      } else {
        searchProductsResult?.push(advertisingData)
        isInsertAdvertising.current = true
      }
    }
    return searchProductsResult
  }, [currentTab, searchProductsResult])

  return (
    <div
      {...rest}
      className={cn(
        'ipc_search_page desktop:py-[128px] tablet:px-8 laptop:px-16 desktop:px-16 lg-desktop:px-[calc(50%-832px)] px-4 py-16',
        className
      )}
    >
      <Heading
        size={4}
        weight="bold"
        html={replaceTemplate(data?.title, {
          totalCount: totalCount?.toString(),
          inputValue: `"${searchValue}"`,
        })}
      />
      <div className="mt-6">
        <Tabs className="ipc_search_page_tabs" defaultValue="products">
          <div className=" desktop:flex desktop:items-center desktop:flex-row desktop:gap-6 desktop:justify-between">
            <TabsList>
              {data?.tabs?.map((tab: any) => (
                <TabsTrigger
                  onClick={() => {
                    setCurrentTab(tab)
                    isInsertAdvertising.current = false
                    // 切换tab时，重置该tab的排序值为默认值（第一个sortKey）
                    const defaultSortKey = tab?.sortKeys?.[0]
                    if (defaultSortKey) {
                      setCurrentSortKey((prev: any) => ({
                        ...prev,
                        [tab?.tabType]: defaultSortKey,
                      }))
                      onChangeSort?.(tab?.tabType, defaultSortKey)
                    }
                    onChangeTab?.(tab?.tabType)
                  }}
                  key={tab.id}
                  defaultValue="products"
                  value={tab.tabType}
                >
                  {tab.label} ({searchResultCount?.[tab.tabType as keyof typeof searchResultCount]})
                </TabsTrigger>
              ))}
            </TabsList>
            <DropdownMenu onOpenChange={setIsOpenSort}>
              <DropdownMenuTrigger asChild>
                <button className="desktop:mt-0 mt-6 flex items-center text-base font-bold leading-[1.2] focus-visible:!outline-none">
                  {currentTab?.sortLabel} {currentSortKey?.[currentTab?.tabType]?.label}{' '}
                  <DownArrow
                    className={cn('inline-block size-4', {
                      'rotate-180': isOpenSort,
                    })}
                  />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="start"
                sideOffset={8}
                className="rounded-sidebar-shelf bg-white !px-0 py-2 focus-visible:!outline-none"
              >
                {currentTab?.sortKeys?.map((sortKey: SortKey) => {
                  return (
                    <DropdownMenuItem
                      className={cn(
                        'laptop:px-4 laptop:text-base px-3 py-4 text-sm font-bold hover:bg-[#EAEAEC] hover:text-current hover:!outline-none',
                        {
                          'bg-[#EAEAEC]': currentSortKey?.[currentTab?.tabType]?.value === sortKey.value,
                        }
                      )}
                      onClick={() => {
                        isInsertAdvertising.current = false
                        onChangeSort?.(currentTab?.tabType, sortKey)
                        setCurrentSortKey((prev: any) => ({
                          ...prev,
                          [currentTab?.tabType]: sortKey,
                        }))
                      }}
                      key={sortKey.id}
                    >
                      {sortKey.label}
                    </DropdownMenuItem>
                  )
                })}
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
          <TabsContent value={SearchPageTabType.PRODUCTS} className="ipc_search_page_tabs_products_content mt-6">
            <div className=" tablet:grid-cols-3 desktop:grid-cols-4 desktop:gap-4 grid grid-cols-2 gap-x-3 gap-y-4">
              {searchProductsResultWithAdvertising?.map((product: any, index: number) => (
                <SearchProductItem
                  searchValue={searchValue}
                  key={(product.id || '') + index}
                  product={product}
                  shopNowLoadingProductId={shopNowLoadingProductId}
                  onSecondaryButton={() => onSecondaryButton?.(product)}
                  onPrimaryButton={() => onPrimaryButton?.(product)}
                />
              ))}
            </div>
            {loading ? (
              <div className="mt-6 flex w-full justify-center">
                <LoadingDots />
              </div>
            ) : (
              !searchProductsResult?.length && (
                <>
                  <SearchPageNoResult url={data?.noResultImage?.url} label={data?.noResultLabel} />
                  <SearchRecommendProducts
                    products={data?.recommendProducts || []}
                    buildProps={buildProps}
                    title={data?.recommendProductsTitle}
                    shopNowLoadingProductId={shopNowLoadingProductId}
                  />
                </>
              )
            )}
          </TabsContent>
          <TabsContent value={SearchPageTabType.BLOGS} className="ipc_search_page_tabs_blogs_content">
            <div>
              {searchBlogsResult?.map((blog: any) => (
                <SearchBlogItem key={blog.id} blog={blog} searchValue={searchValue} />
              ))}
              {loading ? (
                <div className="mt-6 flex justify-center">
                  <LoadingDots />
                </div>
              ) : (
                !searchBlogsResult?.length && (
                  <>
                    <SearchPageNoResult url={data?.noResultImage?.url} label={data?.noResultLabel} />
                    <SearchRecommendProducts
                      products={data?.recommendProducts || []}
                      buildProps={buildProps}
                      title={data?.recommendProductsTitle}
                    />
                  </>
                )
              )}
            </div>
          </TabsContent>
          <TabsContent value={SearchPageTabType.PAGES} className="ipc_search_page_tabs_pages_content">
            <div>
              {searchPagesResult?.map((page: any, index: number) => (
                <SearchPageItem key={(page.global_id || page.id) + index} page={page} searchValue={searchValue} />
              ))}
            </div>
            {loading ? (
              <div className="mt-6 flex justify-center">
                <LoadingDots />
              </div>
            ) : (
              !searchPagesResult?.length && (
                <>
                  <SearchPageNoResult url={data?.noResultImage?.url} label={data?.noResultLabel} />
                  <SearchRecommendProducts
                    products={data?.recommendProducts || []}
                    buildProps={buildProps}
                    title={data?.recommendProductsTitle}
                    shopNowLoadingProductId={shopNowLoadingProductId}
                  />
                </>
              )
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  )
}

export default SearchPage
